import React, {useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import {ProgressInfinite} from '@brandwatch/axiom-components';
import {
  getComponentTypes,
  getPrivateLink,
  loadDeck,
} from '../lib/tycho';
import {getOtp, getCurrentDeck} from '../selectors';
import {fetchPalette} from '../actions/colors';

import auth from '../auth';

import * as storage from '../lib/storage';

import Deck from '../containers/Deck';
import PrivateLinkRequestInvite from './PrivateLinkRequestInvite.jsx';

// Looks in local storage for an OTP that corresponds to the given deck ID.
// This looks through both private links *and* invites. The hope is that an
// OTP will be found that will yield a usable token, meaning the user won't
// need to go through the process of submitting their email address and
// receiving an email invite to the deck.
function getOtpForDeck(deckId) {
  let otp;

  const storageKeys = [
    'privateLinks',
    'invites',
  ];

  for (let i = 0; i < storageKeys.length; i++) {
    const storageKey = storageKeys[i];
    const items = storage.get(storageKey);

    const match = Object.keys(items).find(key => {
      const obj = items[key];

      // Invites can expire, so check that hasn't happened.
      const expiry = !!obj.expiry && moment.utc(obj.expiry);
      const now = moment.utc();
      const hasExpired = !!expiry && now > expiry;

      return obj.deck_id === deckId && !hasExpired;
    });

    if (match) {
      otp = items[match].otp;
      break;
    }
  }

  return otp ? Promise.resolve(otp) : Promise.reject(otp);
}

const PrivateLinkEntryPoint = ({
  match: {
    params: {
      privateLinkIdHash
    }
  }
}) => {
  const [privateLink, setPrivateLink] = useState();

  const dispatch = useDispatch();
  const otp = useSelector(getOtp);

  const [failedToGetToken, setFailedToGetToken] = useState(false);

  // Don't show an error bar at the top of the screen when something goes wrong;
  // in those instances we just want to direct the user through the private link
  // auth flow.
  useEffect(() => {
    dispatch({
      type: 'ALERT_BAR_DISABLE'
    });
  }, [dispatch]);

  // Request basic information about the private link.
  // This is needed to know whether the private link exists / is enabled,
  // and so that the deck's name can be displayed.
  useEffect(() => {
    getPrivateLink(privateLinkIdHash)
      .then(privateLink => {
        setPrivateLink(privateLink);

        dispatch({
          type: 'DECK_ORDER',
          payload: [privateLink.deck_id],
        });
      });
  }, [
    dispatch,
    privateLinkIdHash,
  ]);


  // Once the private link has been loaded (it's a public endpoint),
  // look for an OTP to use. The OTP should be in local storage (via the URL
  // or previously).
  useEffect(() => {
    if (!privateLink) {
      return;
    }

    getOtpForDeck(privateLink.deck_id).then(otp => {
      return auth.init(otp).then(() => {
        dispatch({
          type: 'SET_SEARCH_PARAMS',
          payload: {otp}
        });
      });
    }).catch(() => {
      // eslint-disable-next-line no-console
      console.warn(`Could not initialise auth using OTP: "${otp}". Using private link auth flow instead.`);
      setFailedToGetToken(true);
    });
  }, [
    dispatch,
    privateLink,
    otp,
  ]);

  useEffect(() => {
    if (!otp || !privateLink) {
      return;
    }

    loadDeck(privateLink.deck_id).catch(() => {
      // eslint-disable-next-line no-console
      console.warn(`Could not load deck using OTP: "${otp}".`);
      setFailedToGetToken(true);
    });
    getComponentTypes();
    dispatch(fetchPalette());
  }, [
    otp,
    privateLink,
    setFailedToGetToken,
  ]);

  const handleOtpFailure = useCallback(() => {
    setFailedToGetToken(true);
  }, [setFailedToGetToken]);

  const currentDeck = useSelector(getCurrentDeck);

  // Wait whilst the private link is being loaded and the token is being retrieved.
  if (!privateLink || (!otp && !failedToGetToken) || (!currentDeck.id && !failedToGetToken)) {
    return (
      <div className="Invite vl-flex-row">
        <ProgressInfinite color="white"/>
      </div>
    );
  }

  // If a token has been obtained, or is pending, show / load the Deck.
  if (!failedToGetToken && privateLink.enabled) {
    return (
      <Deck
        id={privateLink.deck_id}
        onOtpFailure={handleOtpFailure}
      />
    );
  }

  // A token couldn't be obtained from the OTP, so the user must go through the
  // private link flow and request an email invite to the deck.
  return (
    <PrivateLinkRequestInvite
      privateLinkIdHash={privateLinkIdHash}
    />
  );
};

PrivateLinkEntryPoint.propTypes = {
  match: PropTypes.object,
};

export default PrivateLinkEntryPoint;
